/**
 * HSV color wheel with hue ring and saturation/value square.
 *
 * Pure canvas rendering, no external dependencies.  Works with both
 * mouse and Apple Pencil pointer events.
 *
 * Accepts and emits hex strings so it drops in wherever
 * `<input type="color">` was used.
 */

import { useCallback, useEffect, useRef, useState } from "react";

// ---------------------------------------------------------------------------
// Color-space helpers
// ---------------------------------------------------------------------------

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.replace("#", ""), 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

function rgbToHex(r: number, g: number, b: number): string {
  return "#" + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
}

function rgbToHsv(r: number, g: number, b: number): [number, number, number] {
  const rf = r / 255,
    gf = g / 255,
    bf = b / 255;
  const max = Math.max(rf, gf, bf),
    min = Math.min(rf, gf, bf);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rf) h = ((gf - bf) / d + 6) % 6;
    else if (max === gf) h = (bf - rf) / d + 2;
    else h = (rf - gf) / d + 4;
    h /= 6;
  }
  const s = max === 0 ? 0 : d / max;
  return [h, s, max];
}

function hsvToRgb(h: number, s: number, v: number): [number, number, number] {
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  let r: number, g: number, b: number;
  switch (i % 6) {
    case 0:
      r = v;
      g = t;
      b = p;
      break;
    case 1:
      r = q;
      g = v;
      b = p;
      break;
    case 2:
      r = p;
      g = v;
      b = t;
      break;
    case 3:
      r = p;
      g = q;
      b = v;
      break;
    case 4:
      r = t;
      g = p;
      b = v;
      break;
    default:
      r = v;
      g = p;
      b = q;
      break;
  }
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface ColorWheelProps {
  /** Current color as a hex string, e.g. "#ff8800". */
  value: string;
  /** Called when the user picks a new color. */
  onChange: (hex: string) => void;
  /** Called once when a pointer-driven color edit starts. */
  onChangeStart?: () => void;
  /** Called once when a pointer-driven color edit ends. */
  onChangeEnd?: () => void;
  /** Diameter of the whole widget in CSS pixels.  Default 120. */
  size?: number;
}

type DragRegion = "hue" | "sv" | null;

export function ColorWheel({
  value,
  onChange,
  onChangeStart,
  onChangeEnd,
  size = 120,
}: ColorWheelProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dragRef = useRef<DragRegion>(null);
  const initialHsv = rgbToHsv(...hexToRgb(value));
  const hsvRef = useRef<[number, number, number]>(initialHsv);

  // Keep HSV in sync with external hex changes
  const [, setTick] = useState(0);
  const lastHexRef = useRef(value);

  if (value !== lastHexRef.current) {
    lastHexRef.current = value;
    const [r, g, b] = hexToRgb(value);
    const hsv = rgbToHsv(r, g, b);
    // Preserve hue when s=0 or v=0 (achromatic)
    if (hsv[1] > 0.001 && hsv[2] > 0.001) hsvRef.current[0] = hsv[0];
    hsvRef.current[1] = hsv[1];
    hsvRef.current[2] = hsv[2];
  }

  const dpr = typeof window !== "undefined" ? window.devicePixelRatio : 1;
  const ringWidth = Math.max(10, size * 0.12);

  // ---------------------------------------------------------------------------
  // Draw
  // ---------------------------------------------------------------------------

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const px = size * dpr;
    canvas.width = px;
    canvas.height = px;
    ctx.clearRect(0, 0, px, px);

    const cx = px / 2;
    const cy = px / 2;
    const outerR = px / 2 - 1;
    const innerR = outerR - ringWidth * dpr;

    // ── Hue ring ──
    const steps = 360;
    for (let i = 0; i < steps; i++) {
      const a0 = ((i - 0.5) / steps) * Math.PI * 2 - Math.PI / 2;
      const a1 = ((i + 0.5) / steps) * Math.PI * 2 - Math.PI / 2;
      ctx.beginPath();
      ctx.arc(cx, cy, (outerR + innerR) / 2, a0, a1 + 0.01);
      ctx.lineWidth = outerR - innerR + 1;
      const [r, g, b] = hsvToRgb(i / steps, 1, 1);
      ctx.strokeStyle = `rgb(${r},${g},${b})`;
      ctx.stroke();
    }

    // ── Hue indicator ──
    const [h, s, v] = hsvRef.current;
    const hueAngle = h * Math.PI * 2 - Math.PI / 2;
    const hueIndicatorR = (outerR + innerR) / 2;
    const hx = cx + Math.cos(hueAngle) * hueIndicatorR;
    const hy = cy + Math.sin(hueAngle) * hueIndicatorR;
    ctx.beginPath();
    ctx.arc(hx, hy, (outerR - innerR) / 2 - 1, 0, Math.PI * 2);
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 2 * dpr;
    ctx.stroke();

    // ── SV square ──
    const sqInset = ringWidth * dpr + 4 * dpr;
    const sqR = (px / 2 - sqInset) * Math.SQRT1_2; // inscribed square half-side
    const sqX = cx - sqR;
    const sqY = cy - sqR;
    const sqSize = sqR * 2;

    // Saturation gradient (left = white, right = hue)
    const [hr, hg, hb] = hsvToRgb(h, 1, 1);
    const gradH = ctx.createLinearGradient(sqX, sqY, sqX + sqSize, sqY);
    gradH.addColorStop(0, "#fff");
    gradH.addColorStop(1, `rgb(${hr},${hg},${hb})`);
    ctx.fillStyle = gradH;
    ctx.fillRect(sqX, sqY, sqSize, sqSize);

    // Value gradient (top = transparent, bottom = black)
    const gradV = ctx.createLinearGradient(sqX, sqY, sqX, sqY + sqSize);
    gradV.addColorStop(0, "rgba(0,0,0,0)");
    gradV.addColorStop(1, "#000");
    ctx.fillStyle = gradV;
    ctx.fillRect(sqX, sqY, sqSize, sqSize);

    // ── SV indicator ──
    const svx = sqX + s * sqSize;
    const svy = sqY + (1 - v) * sqSize;
    ctx.beginPath();
    ctx.arc(svx, svy, 5 * dpr, 0, Math.PI * 2);
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 2 * dpr;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(svx, svy, 3.5 * dpr, 0, Math.PI * 2);
    const [cr, cg, cb] = hsvToRgb(h, s, v);
    ctx.fillStyle = `rgb(${cr},${cg},${cb})`;
    ctx.fill();
  }, [size, dpr, ringWidth]);

  useEffect(draw, [draw]);

  // ---------------------------------------------------------------------------
  // Pointer events
  // ---------------------------------------------------------------------------

  const getRegion = useCallback(
    (ex: number, ey: number): DragRegion => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      const x = ex - rect.left - size / 2;
      const y = ey - rect.top - size / 2;
      const dist = Math.sqrt(x * x + y * y);
      const outerR = size / 2 - 1;
      const innerR = outerR - ringWidth;
      if (dist >= innerR && dist <= outerR + 4) return "hue";
      // SV square
      const sqInset = ringWidth + 4;
      const sqR = (size / 2 - sqInset) * Math.SQRT1_2;
      if (Math.abs(x) <= sqR + 2 && Math.abs(y) <= sqR + 2) return "sv";
      return null;
    },
    [size, ringWidth],
  );

  const updateFromPointer = useCallback(
    (ex: number, ey: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const x = ex - rect.left - size / 2;
      const y = ey - rect.top - size / 2;
      const region = dragRef.current;

      if (region === "hue") {
        let angle = Math.atan2(y, x) + Math.PI / 2;
        if (angle < 0) angle += Math.PI * 2;
        hsvRef.current[0] = angle / (Math.PI * 2);
      } else if (region === "sv") {
        const sqInset = ringWidth + 4;
        const sqR = (size / 2 - sqInset) * Math.SQRT1_2;
        const s = Math.max(0, Math.min(1, (x + sqR) / (sqR * 2)));
        const v = Math.max(0, Math.min(1, 1 - (y + sqR) / (sqR * 2)));
        hsvRef.current[1] = s;
        hsvRef.current[2] = v;
      }

      const [h, s, v] = hsvRef.current;
      const [r, g, b] = hsvToRgb(h, s, v);
      const hex = rgbToHex(r, g, b);
      lastHexRef.current = hex;
      onChange(hex);
      draw();
      setTick((t) => t + 1);
    },
    [size, ringWidth, onChange, draw],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      const region = getRegion(e.clientX, e.clientY);
      if (!region) return;
      e.preventDefault();
      dragRef.current = region;
      onChangeStart?.();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      updateFromPointer(e.clientX, e.clientY);
    },
    [getRegion, onChangeStart, updateFromPointer],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragRef.current) return;
      e.preventDefault();
      updateFromPointer(e.clientX, e.clientY);
    },
    [updateFromPointer],
  );

  const onPointerUp = useCallback(() => {
    if (dragRef.current) onChangeEnd?.();
    dragRef.current = null;
  }, [onChangeEnd]);

  return (
    <canvas
      ref={canvasRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{
        width: size,
        height: size,
        cursor: "crosshair",
        touchAction: "none",
      }}
    />
  );
}
